import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Loader } from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Diario from "@/pages/Diario";
import Percorso from "@/pages/Percorso";
import Simboli from "@/pages/Simboli";
import Profilo from "@/pages/Profilo";
import Abbonamenti from "@/pages/Abbonamenti";
import CheckoutSummary from "@/pages/CheckoutSummary";
import Admin from "@/pages/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import { Terms, Privacy } from "@/pages/Legal";
import IanasStory from "@/pages/IanasStory";
import ChiSono from "@/pages/ChiSono";
import SymbolDetail from "@/pages/SymbolDetail";

function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        window.history.replaceState(null, "", window.location.pathname);
        await checkAuth();
        if (!data.user.onboarding_completed && data.user.role !== "admin") navigate("/onboarding", { replace: true });
        else navigate(data.user.role === "admin" ? "/admin" : "/app", { replace: true });
      } catch {
        navigate("/auth", { replace: true });
      }
    })();
  }, [checkAuth, navigate]);

  return <Loader label="Ti sto accompagnando dentro..." />;
}

function Protected({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (user === false) return <Navigate to="/auth" replace />;
  if (requireOnboarding && !user.onboarding_completed && user.role !== "admin" && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/ianas" element={<IanasStory />} />
      <Route path="/chi-sono" element={<ChiSono />} />
      <Route path="/onboarding" element={<Protected requireOnboarding={false}><Onboarding /></Protected>} />
      <Route path="/app" element={<Protected><Home /></Protected>} />
      <Route path="/app/diario" element={<Protected><Diario /></Protected>} />
      <Route path="/app/percorso" element={<Protected><Percorso /></Protected>} />
      <Route path="/app/simboli" element={<Protected><Simboli /></Protected>} />
      <Route path="/app/simboli/:key" element={<Protected><SymbolDetail /></Protected>} />
      <Route path="/app/profilo" element={<Protected><Profilo /></Protected>} />
      <Route path="/pricing" element={<Protected><Abbonamenti /></Protected>} />
      <Route path="/checkout/:lookup_key" element={<Protected><CheckoutSummary /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/admin" element={<Protected requireOnboarding={false}><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "Manrope, sans-serif" } }} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
